import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, useParams } from "react-router-dom";

import api from "../../services/api";
import { relativeTime, exactTime, formatDate, clockTime } from "../../utils/time";

/** `created_via` on the wire is "api" | "cabinet" (CreatedVia in the backend). */
const VIA_LABELS = { api: "mijoz tizimi API", cabinet: "kabinet" };

/** seq, not sent_at: seq is the thread's own numbering and has no ties. */
const bySeq = (a, b) => a.seq - b.seq;

const DAY_MS = 86400000;
/** How long a silence has to be before the next message starts a new run. */
const RUN_GAP_MS = 15 * 60 * 1000;
const dayKey = (iso) => (iso ? new Date(iso).toDateString() : "");

/** A date separator, the way a chat log breaks up a long history. */
function dayLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const days = Math.round(
    (new Date(new Date().toDateString()) - new Date(d.toDateString())) / DAY_MS,
  );
  if (days === 0) return "Bugun";
  if (days === 1) return "Kecha";
  return formatDate(iso);
}

const initialsOf = (name) =>
  (name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toLocaleUpperCase("uz"))
    .join("");

/**
 * A stable colour per author, so two people in one thread are told apart at a
 * glance. Derived from the PINFL rather than assigned in order, so the same
 * person keeps their colour across threads and page loads.
 */
function hueOf(key) {
  let h = 0;
  for (const ch of String(key || "")) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return h;
}

/**
 * One discussion, with its whole message history.
 *
 * A thread hangs off exactly one object in a client system, so the object it
 * belongs to is not a detail here -- it is the subject. Hence the link out to
 * `object_url` at the top: the document is what the discussion is about, and an
 * auditor reading this page usually needs to open it.
 *
 * The history is shown oldest first. This is a register, not a chat: it is read
 * from the beginning to see how a decision was arrived at, and seq ascending is
 * the order the numbering itself implies.
 */
/**
 * The thread's MySuhbat link, https://thread.mysuhbat.uz/t/<token> (Go
 * migration 0017). The client system stores it and connects the widget with
 * it -- the widget opens a thread by this and by nothing else -- so it is shown
 * whole, with a copy button, rather than clipped in a detail cell.
 */
const ThreadLink = ({ link }) => {
  const [copied, setCopied] = useState(false);
  if (!link) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // No clipboard (an insecure origin): the link is on screen to select.
    }
  };
  return (
    <>
      <div className="ms-secret" style={{ marginTop: 14 }}>
        <span>MySuhbat havolasi</span>
        <code className="ms-mono">{link}</code>
        <button type="button" className="ms-btn ms-btn--ghost ms-btn--sm" onClick={copy}>
          <Icon icon={copied ? "tabler:check" : "tabler:copy"} />
          {copied ? "Nusxa olindi" : "Nusxa olish"}
        </button>
      </div>
      <p className="ms-field-hint" style={{ marginTop: 6 }}>
        Tizimingiz shu havolani obyekt yonida saqlaydi va vidjetni u bilan
        ulaydi. Havola oʻzgarmaydi.
      </p>
    </>
  );
};

const ThreadDetailPage = () => {
  const { id } = useParams();
  const [state, setState] = useState({ loading: true, error: "" });
  const [thread, setThread] = useState(null);
  const [clientName, setClientName] = useState("");
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [older, setOlder] = useState({ loading: false, error: "" });
  const scrollRef = useRef(null);
  const openedAtEnd = useRef(false);
  const keepHeight = useRef(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        // One request, not two: the messages action returns the thread
        // alongside its page of history. include_hidden=1 because a hidden
        // thread is exactly what staff come here to look at -- without it the
        // endpoint 404s on one and the page would claim it never existed.
        const res = await api.get(`/threads/${id}/messages/`, {
          params: { include_hidden: 1 },
        });
        if (!live) return;
        setThread(res.data.thread);
        setMessages([...(res.data.results || [])].sort(bySeq));
        setHasMore(Boolean(res.data.has_more));
        setState({ loading: false, error: "" });
        // Shown, so no longer "Yangi" for me. My own badge only; failing it
        // costs nothing but a badge that lingers until next time.
        api.post(`/threads/${id}/seen/`, null, { params: { include_hidden: 1 } }).catch(() => {});

        const clientId = res.data.thread?.client_id;
        if (!clientId) return;
        try {
          const client = await api.get(`/clients/${clientId}/`);
          if (live) setClientName(client.data.name);
        } catch {
          // The client's NAME is a nicety -- the thread payload already carries
          // its id, and the row below falls back to that. Not worth failing the
          // page over.
        }
      } catch {
        if (live) setState({ loading: false, error: "Muhokamani yuklab boʻlmadi." });
      }
    })();
    return () => {
      live = false;
    };
  }, [id]);

  /** Paged by seq, the way the backend pages: an offset would shift under us. */
  const loadOlder = async () => {
    const oldest = messages[0];
    if (!oldest) return;
    // Measured before the fetch so the layout effect can put the reader back
    // where they were; prepending without this jumps them to the top.
    keepHeight.current = scrollRef.current?.scrollHeight ?? null;
    setOlder({ loading: true, error: "" });
    try {
      const res = await api.get(`/threads/${id}/messages/`, {
        params: { include_hidden: 1, before_seq: oldest.seq },
      });
      setMessages((prev) => [...(res.data.results || []), ...prev].sort(bySeq));
      setHasMore(Boolean(res.data.has_more));
      setOlder({ loading: false, error: "" });
    } catch {
      setOlder({ loading: false, error: "Eski xabarlarni yuklab boʻlmadi." });
    }
  };

  /**
   * Two scroll jobs, and they must not fight.
   *
   * On first paint the log opens on the newest message, the way a chat does --
   * the end of a discussion is what somebody opening it wants. When older
   * messages are prepended the view is held in place instead, because the
   * reader asked for context above what they were already reading.
   */
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !messages.length) return;
    if (keepHeight.current != null) {
      el.scrollTop = el.scrollHeight - keepHeight.current;
      keepHeight.current = null;
      return;
    }
    if (!openedAtEnd.current) {
      el.scrollTop = el.scrollHeight;
      openedAtEnd.current = true;
    }
  }, [messages]);

  if (state.loading) {
    return (
      <div className="ms-panel">
        <div className="ms-state">
          <div className="ms-spinner" />
        </div>
      </div>
    );
  }

  if (state.error || !thread) {
    return (
      <div className="ms-panel">
        <div className="ms-state">
          <Icon icon="tabler:alert-triangle" />
          <span>{state.error || "Muhokama topilmadi."}</span>
        </div>
      </div>
    );
  }

  const deleted = messages.filter((m) => m.is_deleted).length;
  // has_more is false only when the page reached seq 1, so it is also the
  // answer to "is the deleted count below the whole truth or just this page's".
  const allLoaded = !hasMore;

  return (
    <>
      <div className="ms-crumbs">
        <Link to="/muhokamalar">Muhokamalar</Link>
        <Icon icon="tabler:chevron-right" />
        <span>{thread.title || thread.external_object_id}</span>
      </div>

      <div className="ms-recordhead">
        <div className="ms-recordhead-meta">
          <h1>{thread.title || "—"}</h1>
          <span className="ms-mono">{thread.external_object_id}</span>
        </div>
        <div className="ms-toolbar-spacer" />
        <span className={`ms-badge ms-badge--${thread.is_hidden ? "danger" : "success"}`}>
          {thread.is_hidden ? "Yashirilgan" : "Koʻrinadi"}
        </span>
        {thread.object_url && (
          <a
            className="ms-btn ms-btn--ghost ms-btn--sm"
            href={thread.object_url}
            target="_blank"
            rel="noreferrer"
          >
            <Icon icon="tabler:external-link" />
            Obyektni ochish
          </a>
        )}
      </div>

      <div className="ms-recordgrid ms-recordgrid--half">
        <div className="ms-recordcol">
        <div className="ms-stats ms-stats--pair">
          <div className="ms-stat">
            <div className="ms-stat-icon">
              <Icon icon="tabler:messages" />
            </div>
            <div className="ms-stat-body">
              <span>Xabarlar</span>
              <b>{thread.message_count ?? messages.length}</b>
            </div>
          </div>
          <div className="ms-stat">
            <div className="ms-stat-icon is-danger">
              <Icon icon="tabler:trash" />
            </div>
            <div className="ms-stat-body">
              <span>Oʻchirilgan</span>
              <b title={allLoaded ? "" : "Faqat yuklangan xabarlar boʻyicha"}>
                {deleted}
                {allLoaded ? "" : "+"}
              </b>
            </div>
          </div>
          <div className="ms-stat">
            <div className="ms-stat-icon">
              <Icon icon="tabler:hash" />
            </div>
            <div className="ms-stat-body">
              <span>Oxirgi raqam</span>
              <b>{thread.last_seq ?? 0}</b>
            </div>
          </div>
          <div className="ms-stat">
            <div className="ms-stat-icon">
              <Icon icon="tabler:clock" />
            </div>
            <div className="ms-stat-body">
              <span>Oxirgi xabar</span>
              <b title={exactTime(thread.last_message_at)}>
                {thread.last_message_at ? relativeTime(thread.last_message_at) : "—"}
              </b>
            </div>
          </div>
        </div>

          <div className="ms-panel">
            <div className="ms-panel-head">
              <Icon icon="tabler:info-circle" />
              <h2>Muhokama</h2>
            </div>
            <div className="ms-panel-body">
              <dl className="ms-detail">
                <div className="ms-detail-row">
                  <dt>Mijoz tizimi</dt>
                  <dd>
                    {/* Plain text: the cabinet has no client page to link to --
                        this is the only system its reader has. */}
                    {clientName || thread.client_id}
                  </dd>
                </div>
                <div className="ms-detail-row">
                  <dt>Guruh</dt>
                  <dd>{thread.group_name || "—"}</dd>
                </div>
                <div className="ms-detail-row ms-detail-row--full">
                  <dt>Tashqi obyekt</dt>
                  <dd className="ms-mono">{thread.external_object_id}</dd>
                </div>
                <div className="ms-detail-row">
                  <dt>Qanday yaratilgan</dt>
                  <dd>
                    <span className="ms-badge ms-badge--neutral">
                      {VIA_LABELS[thread.created_via] || thread.created_via || "—"}
                    </span>
                  </dd>
                </div>
                <div className="ms-detail-row">
                  <dt>Yaratilgan</dt>
                  <dd title={exactTime(thread.created_at)}>{relativeTime(thread.created_at)}</dd>
                </div>
                {thread.is_hidden && (
                  <div className="ms-detail-row">
                    <dt>Yashirilgan</dt>
                    <dd title={exactTime(thread.deleted_at)}>
                      {relativeTime(thread.deleted_at)}
                    </dd>
                  </div>
                )}
                <div className="ms-detail-row ms-detail-row--full">
                  <dt>Muhokama ID</dt>
                  <dd className="ms-mono">{thread.id}</dd>
                </div>
              </dl>

              <ThreadLink link={thread.link} />

              {/* The address is long and is the one thing on this page a reader
                  is likely to follow, so it gets its own block instead of being
                  clipped inside a detail cell. */}
              <div className="ms-note">
                <span>Obyekt manzili</span>
                <p>
                  {thread.object_url ? (
                    <a href={thread.object_url} target="_blank" rel="noreferrer">
                      {thread.object_url}
                    </a>
                  ) : (
                    "Manzil koʻrsatilmagan"
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="ms-recordcol">
          <div className="ms-panel">
            <div className="ms-panel-head">
              <Icon icon="tabler:history" />
              <h2>Xabarlar tarixi</h2>
              <div className="ms-toolbar-spacer" />
              <span className="ms-badge ms-badge--neutral">
                {messages.length} / {thread.message_count ?? messages.length} ta
              </span>
            </div>

            {messages.length === 0 ? (
              <div className="ms-state">
                <Icon icon="tabler:message-off" />
                <span>Bu muhokamada hali xabar yoʻq</span>
              </div>
            ) : (
              <div className="ms-chat" ref={scrollRef}>
                {/* Oldest first, so "older" is up: the button sits where the
                    messages it fetches will appear. */}
                {hasMore && (
                  <div className="ms-chat-more">
                    <button
                      type="button"
                      className="ms-btn ms-btn--ghost ms-btn--sm"
                      onClick={loadOlder}
                      disabled={older.loading}
                    >
                      <Icon icon="tabler:arrow-up" />
                      {older.loading ? "Yuklanmoqda…" : "Eskilarini yuklash"}
                    </button>
                  </div>
                )}

                {older.error && (
                  <div className="ms-state" style={{ padding: "12px 0" }}>
                    <Icon icon="tabler:alert-triangle" />
                    <span>{older.error}</span>
                  </div>
                )}

                {messages.map((m, i) => {
                  const prev = messages[i - 1];
                  const newDay = !prev || dayKey(prev.sent_at) !== dayKey(m.sent_at);
                  // A run by one author collapses into a single block. Repeating
                  // the name and PINFL on every line is what made this read as a
                  // table rather than a conversation.
                  //
                  // A long pause also breaks the run, even for the same author:
                  // a thread that is one person posting over two hours would
                  // otherwise show the name once, at the top, and every screen
                  // after it would be anonymous.
                  const gap = prev ? new Date(m.sent_at) - new Date(prev.sent_at) : 0;
                  const opensRun =
                    newDay || !prev || prev.author_pinfl !== m.author_pinfl || gap > RUN_GAP_MS;

                  return (
                    <Fragment key={m.id}>
                      {newDay && (
                        <div className="ms-chat-day">
                          <span>{dayLabel(m.sent_at)}</span>
                        </div>
                      )}

                      <div className={`ms-chat-row${opensRun ? " opens-run" : ""}`}>
                        <div
                          className="ms-chat-avatar"
                          aria-hidden="true"
                          style={
                            opensRun
                              ? { background: `hsl(${hueOf(m.author_pinfl)} 52% 42%)` }
                              : undefined
                          }
                        >
                          {opensRun ? initialsOf(m.author_name) : ""}
                        </div>

                        <div className="ms-chat-col">
                          {opensRun && (
                            <div className="ms-chat-who">
                              <b>{m.author_name || "Nomaʼlum"}</b>
                              {m.author_pinfl && (
                                <span className="ms-mono">{m.author_pinfl}</span>
                              )}
                            </div>
                          )}

                          {/* A deleted message keeps its place in the log. The
                              API blanks the body on purpose -- showing text
                              somebody deleted is a support incident -- but that
                              a message was written, by whom and when, is the
                              audit record and stays. */}
                          <div
                            className={`ms-chat-bubble${m.is_deleted ? " is-deleted" : ""}`}
                          >
                            {m.is_deleted ? (
                              <span className="ms-chat-gone">
                                Matn muallif tomonidan oʻchirilgan
                              </span>
                            ) : (
                              <span className="ms-chat-text">{m.body || "—"}</span>
                            )}

                            <span className="ms-chat-meta">
                              {m.is_deleted && (
                                <span
                                  className="ms-chat-flag"
                                  title={`Oʻchirilgan: ${exactTime(m.deleted_at)}`}
                                >
                                  oʻchirilgan
                                </span>
                              )}
                              {/* seq is the audit key -- kept, but quiet. */}
                              <span className="ms-chat-seq">#{m.seq}</span>
                              <time title={exactTime(m.sent_at)}>{clockTime(m.sent_at)}</time>
                            </span>
                          </div>
                        </div>
                      </div>
                    </Fragment>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ThreadDetailPage;
